import type { CoreEvent } from './types.js';

/**
 * Canonical event ordering.
 *
 * BUILD_PLAN 5.8 specifies `(period, game_clock_ms desc, wall_clock)`. That is
 * correct but not a total order, and a non-total order means two devices can
 * derive two different box scores from the same event set. The remaining keys
 * below exist to make it total and therefore reproducible.
 *
 * Order of keys, and why each one:
 *
 * 1. `period` ascending. Periods are sequential.
 * 2. `gameClockMs` DESCENDING. The clock counts down, so a larger remaining
 *    value happened earlier.
 * 3. `wallClock` ascending. Two devices scoring the same game share no sequence
 *    counter, so real time is the only thing that orders across them.
 * 4. `deviceId` ascending. Ties at the same wall-clock millisecond across
 *    devices need a stable tiebreak that both devices compute identically.
 * 5. `clientSeq` ascending. Within one device this is definitive: it is the
 *    order the scorekeeper's thumb actually produced, which matters when a
 *    substitution and a basket share a clock reading.
 * 6. `id` ascending. Last resort, guarantees totality.
 *
 * Sorting is stable and does not mutate the input.
 */
export function orderEvents(events: readonly CoreEvent[]): readonly CoreEvent[] {
  return [...events].sort(compareEvents);
}

export function compareEvents(a: CoreEvent, b: CoreEvent): number {
  if (a.period !== b.period) return a.period - b.period;
  if (a.gameClockMs !== b.gameClockMs) return b.gameClockMs - a.gameClockMs;
  if (a.wallClock !== b.wallClock) return a.wallClock < b.wallClock ? -1 : 1;
  if (a.deviceId !== b.deviceId) return a.deviceId < b.deviceId ? -1 : 1;
  if (a.clientSeq !== b.clientSeq) return a.clientSeq - b.clientSeq;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * Drop voided events.
 *
 * A correction is a void plus a new event, never a mutation (REVIEW_RUBRIC A1).
 * The voided row stays in the log forever so the audit trail is intact and undo
 * is reversible; it just does not count toward anything derived.
 */
export function activeEvents(events: readonly CoreEvent[]): readonly CoreEvent[] {
  return events.filter((event) => event.voidedAt === null);
}

/**
 * Order and filter in one pass, which is what every derivation wants.
 */
export function normalizeEvents(events: readonly CoreEvent[]): readonly CoreEvent[] {
  return orderEvents(activeEvents(events));
}

/**
 * Group consecutive events that share a period and clock reading.
 *
 * A substitution writes SUB_OUT and SUB_IN at the same `gameClockMs`
 * (BUILD_PLAN 5.5), and a bulk sub writes six or more. Applying them one at a
 * time would produce a lineup that is briefly four players and a stint of zero
 * length, which then pollutes plus-minus. Applying them as a group is both
 * correct and what actually happened on the floor.
 */
export interface ClockGroup {
  readonly period: number;
  readonly gameClockMs: number;
  readonly events: readonly CoreEvent[];
}

export function groupByClockReading(events: readonly CoreEvent[]): readonly ClockGroup[] {
  const groups: { period: number; gameClockMs: number; events: CoreEvent[] }[] = [];

  for (const event of events) {
    const current = groups.at(-1);
    if (
      current !== undefined &&
      current.period === event.period &&
      current.gameClockMs === event.gameClockMs
    ) {
      current.events.push(event);
    } else {
      groups.push({ period: event.period, gameClockMs: event.gameClockMs, events: [event] });
    }
  }

  return groups;
}
